/**
 * Selling fast: a chip in the top-right badge slot of a colour that's selling quickly.
 * Rule and list: Swyft-Desk desk/plans/2026-10-14-selling-fast-rule.md; the list is the shop's swyft.selling_fast metafield,
 * refreshed every morning, read in snippets/swyft-tests.liquid.
 *
 * Theme setting "Selling fast badge": off (this file isn't loaded), test (selling-fast-v1 decides; both arms are counted
 * when a qualifying colour is shown), or on for everyone (nothing counted).
 *
 * One badge per image: the chip only takes a slot that is empty or says Sale. New In, Clearance and Top Deal win.
 */
(() => {
  const TEST = 'selling-fast-v1';
  const STRONGER = /new in|clearance|top deal|sold out/i;

  const config = (() => {
    try {
      return JSON.parse(document.getElementById('swyft-tests').textContent);
    } catch (e) {
      return null;
    }
  })();
  const sf = config && config.sellingFast;
  if (!sf || sf.mode === 'off' || !sf.handles.length) return;
  const handles = new Set(sf.handles);
  const label = (config.copy && config.copy.sellingFast) || 'Selling fast';

  const chip = () => {
    const el = document.createElement('span');
    el.className = 'badge swyft-selling-fast';
    el.innerHTML = '<span class="swyft-selling-fast__dot" aria-hidden="true"></span>';
    el.append(label);
    return el;
  };

  // null: show nothing and count nothing. 'A' and 'B' are counted; only B sees the chip.
  const armFor = (() => {
    let pending = null;
    return () => {
      if (sf.mode === 'on') return Promise.resolve('on');
      if (!pending) pending = window.SwyftTest ? window.SwyftTest.arm(TEST) : Promise.resolve(null);
      return pending;
    };
  })();

  const expose = (arm, detail) => {
    if (arm === 'A' || arm === 'B') window.SwyftTest.exposure(TEST, arm, { text: label, ...detail });
  };

  const variantFrom = (href) => {
    try {
      return new URL(href, location.origin).searchParams.get('variant');
    } catch (e) {
      return null;
    }
  };

  // Cards: Graphene draws collection and search grids, so cards are found as they arrive and counted once half in view.
  const seen = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        seen.unobserve(entry.target);
        const card = entry.target;
        armFor().then((arm) =>
          expose(arm, { surface: 'card', product_id: null, variant_id: variantFrom(card.querySelector('a[href*="/products/"]')?.getAttribute('href')) })
        );
      }
    },
    { threshold: 0.5 }
  );

  async function card(item) {
    item.dataset.sellingFast = 'checked';
    if (!handles.has(item.dataset.productHandle)) return;
    const inner = item.querySelector('.card__inner');
    if (!inner) return;
    const slot = inner.querySelector('.card__badge');
    const existing = slot && slot.querySelector('.badge');
    if (existing && STRONGER.test(existing.textContent)) return;

    const arm = await armFor();
    if (!arm) return;
    // Top Deal may have taken the slot while the arm was being decided (assets/swyft-top-deal.js).
    if (existing && STRONGER.test(existing.textContent)) return;
    if (arm === 'A' || arm === 'B') seen.observe(item);
    if (arm === 'A') return;

    if (slot) {
      if (existing) existing.classList.add('swyft-selling-fast__replaced');
      slot.append(chip());
    } else {
      const added = document.createElement('div');
      added.className = 'card__badge top right absolute';
      added.append(chip());
      inner.append(added);
    }
  }

  let queued = false;
  const scan = () => {
    queued = false;
    document.querySelectorAll('li[data-product-handle]:not([data-selling-fast])').forEach(card);
  };
  const queue = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(scan);
  };

  // Product page: the theme decided in Liquid whether this colour qualifies, where its own badges are known.
  async function productPage() {
    if (!sf.pdp) return;
    const arm = await armFor();
    if (!arm) return;
    const variant = document.querySelector('product-info input[name="id"], form[action*="/cart/add"] input[name="id"]');
    expose(arm, {
      surface: 'pdp',
      product_id: document.querySelector('product-info')?.dataset.productId || window.ShopifyAnalytics?.meta?.product?.id || null,
      variant_id: variant ? variant.value : null,
    });
    if (arm === 'A') return;
    // The main image viewer, not the thumbnail strip under it.
    const gallery = document.querySelector('.product__media-wrapper slider-component[id^="GalleryViewer"]');
    if (!gallery || gallery.querySelector('.swyft-selling-fast')) return;
    // On phones the price's Sale badge sits on the gallery corner; the chip takes that slot.
    document.querySelectorAll('.product__media-wrapper .price__badge-sale').forEach((b) => b.classList.add('swyft-selling-fast__replaced'));
    const slot = document.createElement('div');
    slot.className = 'swyft-selling-fast__pdp';
    slot.append(chip());
    gallery.append(slot);
  }

  const start = () => {
    productPage();
    scan();
    new MutationObserver(queue).observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
